import * as React from "react";

import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  useTheme,
  Stack,
  TextField,
} from "@mui/material";
import { AppContext } from "../../contextProviders/AppContext";

import {
  addHours,
  getHours,
  getMinutes,
  isBefore,
  isSameDay,
  isValid,
  roundToNearestMinutes,
  set,
} from "date-fns";
import DatePickerMobile from "./DatePickerMobile";
import { translation } from "../Dictionary";
import { useContext } from "react";
import { useMemo } from "react";

export default function DefaultDatePicker({
  companyOpenHours,
  companyOpenHoursReturn,
  getMinimumDate,
  openDateRangeMobile,
  returnTimeLabel,
  startTimeLabel,
  setOpenDateRangeMobile,
  startDateString,
  VUOKRAAPAKU_SPECIAL_COMPANY,
  localeText,
}) {
  const {
    setSelectedDateRange,
    selectedCompany,
    selectedPicker,
    setSelectedPicker,
    selectedDateRange,
    mobileViewPort,
    startDayBusinessHours,
  } = React.useContext(AppContext);

  const deliveryBufferTime = React.useMemo(() => {
    if (!selectedCompany?.deliveryBufferHours) {
      return set(addHours(new Date(), 1), { minutes: 0 });
    }
    // TODO, in some cases, the buffer time isnt correct when changing companies, (from buffer of 4 to buffer of 24)
    return set(addHours(new Date(), selectedCompany?.deliveryBufferHours + 1), {
      minutes: getMinutes(roundToNearestMinutes(new Date(), { nearestTo: 15 })),
    });
  }, [selectedCompany?.deliveryBufferHours]);

  const handleChangeDate = (newValue) => {
    const setStartTime = (date) => {
      if (!isValid(date)) {
        return null;
      }
      if (isSameDay(date, deliveryBufferTime)) {
        return deliveryBufferTime;
      } else {
        const openHour = set(date, {
          hours:
            startDayBusinessHours(selectedCompany, newValue[0])?.openAt?.split(
              ":"
            )[0] ?? getHours(date),
          minutes:
            startDayBusinessHours(selectedCompany, newValue[0])?.openAt.split(
              ":"
            )[1] ?? getMinutes(date),
        });

        if (isBefore(deliveryBufferTime, openHour)) {
          if (getHours(openHour) === 0) {
            // dont set the time to midnight by default
            return set(date, { hours: getHours(new Date()) });
          } else {
            return openHour;
          }
        }

        /*    if (isBefore(date, deliveryBufferTime)) {
        console.log("TRIGGER date < deliveryBufferTime");
        return deliveryBufferTime;
      }
 */
        if (getHours(openHour) === 0) {
          // dont set the time to midnight by default
          return set(date, { hours: getHours(new Date()) });
        } else {
          return openHour;
        }
      }
    };
    const start = setStartTime(newValue[0]);

    const setEndTime = (date) => {
      if (!start) {
        return null;
      }

      if (!date) {
        return addHours(start, 6);
      }

      if (isBefore(date, start)) {
        return addHours(start, 6);
      }
      return set(date, {
        hours: getHours(start) + 6,
        minutes: getMinutes(roundToNearestMinutes(start, { nearestTo: 15 })),
      });
    };

    const end = setEndTime(newValue[1]);
    const datesWithTime = [start, end];
    return setSelectedDateRange(datesWithTime);
  };

  React.useEffect(() => {
    handleChangeDate(selectedDateRange);
  }, [selectedCompany]);

  if (!mobileViewPort) {
    return (
      <DateRangePicker
        slots={{
          toolbar: PickerToolbar,
          actionBar: ActionList,
          textField: CustomTextField,
        }}
        slotProps={{
          textField: ({ position }) => ({}),
          fieldSeparator: {
            children: "",
          },
        }}
        disablePast
        onOpen={() => setSelectedPicker("calendar")}
        open={selectedPicker === "calendar"}
        onClose={() => setSelectedPicker("startTime")}
        disableOpenPicker={!selectedCompany}
        value={selectedDateRange}
        localeText={localeText}
        onChange={(newValue) => {
          handleChangeDate(newValue);
        }}
        shouldDisableDate={(newValue, position) => {
          // check for weekly open hours of company & businessHoursExceptions

          if (position === "start") {
            /*       if (!startDayBusinessHours(selectedCompany, newValue)) {
              return true;
            } */

            // check for delivery buffer time
            if (
              isBefore(
                set(newValue, {
                  hours: getHours(deliveryBufferTime),
                  minutes: getMinutes(deliveryBufferTime),
                  seconds: 59,
                }),
                deliveryBufferTime
              )
            ) {
              return true;
            }
            return false;
          }
          if (position === "end") {
            if (
              isBefore(newValue, selectedDateRange[0]) &&
              !isSameDay(newValue, selectedDateRange[0])
            ) {
              return true;
            }
          }
        }}
      />
    );
  } else {
    return (
      <DatePickerMobile
        selectedPicker={selectedPicker}
        setSelectedPicker={setSelectedPicker}
        companyOpenHours={companyOpenHours}
        companyOpenHoursReturn={companyOpenHoursReturn}
        getMinimumDate={getMinimumDate}
        openDateRangeMobile={openDateRangeMobile}
        returnTimeLabel={returnTimeLabel}
        setOpenDateRangeMobile={setOpenDateRangeMobile}
        startDateString={startDateString}
        VUOKRAAPAKU_SPECIAL_COMPANY={VUOKRAAPAKU_SPECIAL_COMPANY}
        handleChangeDate={handleChangeDate}
        deliveryBufferTime={deliveryBufferTime}
        startTimeLabel={startTimeLabel}
      />
    );
  }
}

function ActionList(props) {
  const { onAccept, className } = props;
  const actions = [{ text: "Valmis", method: onAccept }];
  return (
    // Propagate the className such that CSS selectors can be applied
    <Box
      className={className}
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-end",
      }}
    >
      {actions.map(({ text, method }) => (
        <List key={text} sx={{ margin: "5px", marginBottom: "10px" }}>
          <ListItemButton
            onClick={method}
            sx={{
              paddingY: "5px",
              borderRadius: "7px",
            }}
          >
            <ListItemText primary={text} />
          </ListItemButton>
        </List>
      ))}
    </Box>
  );
}

function PickerToolbar(props) {
  const { language, selectedCompany } = useContext(AppContext);
  const { className } = props;
  const theme = useTheme();
  const pickerLabel = () => {
    const start = props?.value[0];
    const end = props?.value[1];
    if (!isValid(start)) {
      return translation.get(language, "Select start date");
    }
    if (isValid(end)) {
      return translation.get(language, "Reservation timeframe selected");
    } else {
      return translation.get(language, "Select return date");
    }
  };

  const deliveryBufferHoursLabel = useMemo(() => {
    if (selectedCompany?.deliveryBufferHours) {
      if (language === "fi") {
        return (
          <Typography variant="caption">
            Varauksen voi tehdä aikaisintaan{" "}
            {selectedCompany?.deliveryBufferHours} tunnin kuluttua nykyhetkestä.
          </Typography>
        );
      }
      if (language === "se") {
        return (
          <Typography variant="caption">
            Du kan göra en reservation tidigast{" "}
            {selectedCompany?.deliveryBufferHours} timmar från nuvarande
            ögonblick.
          </Typography>
        );
      }
      if (language === "en") {
        return (
          <Typography variant="caption">
            You can make a reservation no earlier than{" "}
            {selectedCompany?.deliveryBufferHours} hours from the current
            moment.
          </Typography>
        );
      }
    }
    return null;
  }, [language, selectedCompany?.deliveryBufferHours]);

  const titleBgColor = () => {
    const start = props?.value[0];
    const end = props?.value[1];
    if (!start) {
      return theme.palette.primary.dateRangeBgFrom;
    }
    if (start && !end) {
      return theme.palette.primary.dateRangeBgTo;
    }

    if (start && end) {
      return theme.palette.primary.dateRangeBgComplete;
    }

    return theme.palette.info.main;
  };

  return (
    <Box className={className}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "10px",
        }}
      >
        <Stack
          direction={"column"}
          alignItems={"center"}
          justifyContent={"center"}
          sx={{
            bgcolor: titleBgColor(),
            color: theme.palette.primary.secondaryText,

            paddingY: "10px",

            borderRadius: "10px",
            width: "75%",
            boxShadow: theme.shadows[3],
          }}
        >
          <Typography
            sx={{
              fontFamily: "Sofia Pro",
              fontSize: "16px",
            }}
          >
            {pickerLabel()}
          </Typography>
        </Stack>
        {deliveryBufferHoursLabel}
      </Box>
    </Box>
  );
}

const CustomTextField = (props) => {
  const theme = useTheme();

  return (
    <TextField
      {...props}
      InputProps={{
        ...props.InputProps,
        disableUnderline: true,
        style: {
          borderRadius: "10px",
          backgroundColor: "transparent",
          border: "none",
        },
      }}
      inputProps={{
        ...props.inputProps,
        readOnly: true,
        style: { cursor: "pointer" },
        placeholder: "",
      }}
      sx={{
        input: {
          color: theme.palette.primary.mainText,
          borderRadius: "10px",
          minWidth: "70px",
          maxWidth: "120px",
          cursor: "pointer",
          transition: "150ms ease-in",
          "&:hover": {
            bgcolor: "rgba(0,0,0,0.05)",
            boxShadow: theme.shadows[2],
          },
        },
      }}
      variant="filled"
    />
  );
};
